"use client"

import { useQuery } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration
const API_BASE = ""

export interface AuditLogUser {
  id: string
  name: string | null
  email: string
  avatarUrl: string | null
}

export interface AuditLog {
  id: string
  action: string
  resource: string
  resourceId: string | null
  oldValue: Record<string, unknown> | null
  newValue: Record<string, unknown> | null
  timestamp: string
  user: AuditLogUser | null
}

export interface AuditLogOptions {
  page?: number
  pageSize?: number
  action?: string
  resource?: string
  startDate?: string
  endDate?: string
}

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

async function fetchAuditLogs(
  projectId: string,
  token: string | null,
  options: AuditLogOptions = {}
): Promise<{ logs: AuditLog[]; total: number; page: number; pageSize: number; totalPages: number }> {
  if (!token) {
    return { logs: [], total: 0, page: 1, pageSize: 20, totalPages: 0 }
  }

  const params = new URLSearchParams()
  if (options.page) params.set("page", options.page.toString())
  if (options.pageSize) params.set("pageSize", options.pageSize.toString())
  if (options.action) params.set("action", options.action)
  if (options.resource) params.set("resource", options.resource)
  if (options.startDate) params.set("startDate", options.startDate)
  if (options.endDate) params.set("endDate", options.endDate)

  const url = `${API_BASE}/api/projects/${projectId}/audit-logs${params.toString() ? `?${params.toString()}` : ""}`

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 401 || response.status === 403 || response.status === 404) {
      return { logs: [], total: 0, page: 1, pageSize: 20, totalPages: 0 }
    }
    throw new Error("Failed to fetch audit logs")
  }

  const data: PaginatedResponse<AuditLog> = await response.json()
  return {
    logs: data.data,
    total: data.meta.pagination.total,
    page: data.meta.pagination.page,
    pageSize: data.meta.pagination.pageSize,
    totalPages: data.meta.pagination.totalPages,
  }
}

/**
 * Hook to fetch audit logs for a project
 * Only accessible by project admins and PMs
 */
export function useAuditLogs(projectId: string, options: AuditLogOptions = {}) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["projects", projectId, "audit-logs", options],
    queryFn: () => fetchAuditLogs(projectId, token, options),
    enabled: !!projectId && !!token,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Get a human-readable label for an audit action
 */
export function getActionLabel(action: string): string {
  const actionLabels: Record<string, string> = {
    // Project Events
    ProjectCreated: "Created project",
    ProjectUpdated: "Updated project",
    ProjectDeleted: "Deleted project",
    // Membership Events
    MemberAdded: "Added member",
    MemberRemoved: "Removed member",
    MemberRoleChanged: "Changed member role",
    // Document Events
    FilesUploaded: "Uploaded files",
    DocumentProcessed: "Processed document",
    DocumentDeleted: "Deleted document",
    // Artifact Events
    ArtefactCreated: "Created artifact",
    ArtefactUpdated: "Updated artifact",
    ArtefactStatusChanged: "Changed artifact status",
    ArtefactApproved: "Approved artifact",
    ArtefactRejected: "Rejected artifact",
    // Template Events
    TemplateCreated: "Created template",
    TemplateEdited: "Edited template",
    TemplateVersionPublished: "Published template version",
    // Generic
    CREATE: "Created",
    UPDATE: "Updated",
    DELETE: "Deleted",
    ARCHIVE: "Archived",
  }
  return actionLabels[action] || action
}

/**
 * Get a human-readable label for a resource type
 */
export function getResourceLabel(resource: string): string {
  const resourceLabels: Record<string, string> = {
    project: "Project",
    document: "Document",
    goal: "Goal",
    persona: "Persona",
    journey: "Journey",
    interview: "Interview",
    competitor: "Competitor",
    use_case: "Use Case",
    service_map: "Service Map",
    storyboard: "Storyboard",
    story: "Story",
    template: "Template",
    definition_persona: "Persona",
    definition_use_case: "Use Case",
    definition_journey: "Journey",
    definition_service_map: "Service Map",
    definition_storyboard: "Storyboard",
  }
  return resourceLabels[resource] || resource
}
